import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Loader2, AlertCircle, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Form genérico para abrir chamado (troca/manutencao/devolucao/aditivo).
 *
 * Props:
 *  tipo: "troca" | "manutencao" | "devolucao" | "aditivo"
 *  title, subtitle, alert (cor: info|warning|danger)
 *  motivos: array de strings (dropdown)
 *  requiresUnidade: bool
 *  showUrgencia: bool
 *  showFoto: bool
 */
export default function TicketForm({
  tipo,
  title,
  subtitle,
  alert,
  motivos,
  requiresUnidade = true,
  showUrgencia = false,
  showFoto = false,
}) {
  const nav = useNavigate();
  const [frota, setFrota] = useState([]);
  const [unidadeId, setUnidadeId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [motivoOutro, setMotivoOutro] = useState("");
  const [descricao, setDescricao] = useState("");
  const [urgencia, setUrgencia] = useState("Normal");
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!requiresUnidade) return;
    api.get("/portal/minha-frota").then((r) => {
      // troca/manutencao/devolucao só podem usar unidades não-extraviadas
      const usaveis = (r.data.unidades || []).filter((u) => u.status !== "extraviada");
      setFrota(usaveis);
    });
  }, [requiresUnidade]);

  const submit = async (e) => {
    e?.preventDefault();
    if (requiresUnidade && !unidadeId) {
      toast.error("Selecione um equipamento da sua frota");
      return;
    }
    const finalMotivo = motivo === "Outro" ? motivoOutro : motivo;
    if (!finalMotivo) {
      toast.error("Informe o motivo");
      return;
    }
    setBusy(true);
    setAnalyzing(true);
    try {
      // pequena espera visual para o usuário ver "Analisando..."
      const payload = {
        tipo,
        unidade_id: requiresUnidade ? unidadeId : null,
        motivo: finalMotivo,
        descricao,
        urgencia: showUrgencia ? urgencia : undefined,
      };
      const { data } = await api.post("/portal/tickets", payload);
      setResult(data);
      toast.success("Chamado registrado! Nossa equipe foi notificada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao abrir chamado");
    } finally {
      setBusy(false);
      setAnalyzing(false);
    }
  };

  if (result) {
    return (
      <Card className="max-w-2xl mx-auto p-8 text-center" data-testid="ticket-success">
        <div
          className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white mb-4"
          style={{ background: "#F97316" }}
        >
          <Sparkles size={28} />
        </div>
        <h2 className="text-2xl font-display font-bold mb-1" style={{ color: "#1B3A6B" }}>
          Chamado registrado!
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Protocolo <span className="font-mono font-bold">#{result.id.slice(0, 6).toUpperCase()}</span>
        </p>
        <div className="flex flex-wrap gap-2 justify-center mb-5">
          <Badge className={`px-3 py-1 ${
            result.urgencia_ia === "Alta" ? "bg-red-100 text-red-700"
              : result.urgencia_ia === "Baixa" ? "bg-green-100 text-green-700"
              : "bg-yellow-100 text-yellow-800"
          }`}>
            Urgência: {result.urgencia_ia}
          </Badge>
          <Badge variant="outline">Tipo: {result.tipo_ia}</Badge>
        </div>
        <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
          <strong>Próxima ação:</strong> {result.acao_ia}
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => nav("/cliente/home")}
            data-testid="ticket-success-home"
          >
            Voltar ao início
          </Button>
          <Button
            onClick={() => nav("/cliente/chamados")}
            data-testid="ticket-success-list"
            className="text-white"
            style={{ background: "#F97316" }}
          >
            Ver meus chamados
          </Button>
        </div>
      </Card>
    );
  }

  const alertBg = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    danger: "bg-red-50 border-red-200 text-red-800",
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => nav("/cliente/home")}
        className="mb-3 text-gray-500"
        data-testid="ticket-form-back"
      >
        <ArrowLeft size={14} className="mr-1" /> Voltar
      </Button>
      <Card className="p-6 sm:p-8">
        <h1 className="text-2xl font-display font-bold mb-1" style={{ color: "#1B3A6B" }}>
          {title}
        </h1>
        <p className="text-sm text-gray-500 mb-5">{subtitle}</p>

        {alert && (
          <div className={`flex gap-2 items-start text-sm rounded-md border p-3 mb-5 ${alertBg[alert.tipo] || alertBg.info}`}>
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{alert.texto}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {requiresUnidade && (
            <div>
              <Label>Equipamento *</Label>
              <Select value={unidadeId} onValueChange={setUnidadeId}>
                <SelectTrigger className="mt-1.5" data-testid="ticket-unidade-select">
                  <SelectValue placeholder="Selecione um equipamento da sua frota" />
                </SelectTrigger>
                <SelectContent>
                  {frota.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Nenhum equipamento disponível
                    </div>
                  )}
                  {frota.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`ticket-unidade-opt-${u.id}`}>
                      {u.modelo_nome} · #{u.numero_serie}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Motivo *</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger className="mt-1.5" data-testid="ticket-motivo-select">
                <SelectValue placeholder="Selecione o motivo" />
              </SelectTrigger>
              <SelectContent>
                {motivos.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
                <SelectItem value="Outro">Outro</SelectItem>
              </SelectContent>
            </Select>
            {motivo === "Outro" && (
              <input
                type="text"
                className="mt-2 w-full border rounded-md px-3 py-2 text-sm"
                placeholder="Descreva o motivo"
                value={motivoOutro}
                data-testid="ticket-motivo-outro"
                onChange={(e) => setMotivoOutro(e.target.value)}
              />
            )}
          </div>

          <div>
            <Label>Descrição</Label>
            <Textarea
              data-testid="ticket-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Conte mais detalhes para nossa equipe..."
              rows={4}
              className="mt-1.5"
            />
          </div>

          {showUrgencia && (
            <div>
              <Label>Urgência percebida</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="ticket-urgencia-group">
                {["Normal", "Urgente", "Muito urgente"].map((u) => (
                  <button
                    type="button"
                    key={u}
                    onClick={() => setUrgencia(u)}
                    data-testid={`ticket-urgencia-${u.toLowerCase().replace(/\s/g, "-")}`}
                    className={`px-3 py-2 text-xs sm:text-sm rounded-md border transition ${
                      urgencia === u
                        ? "border-orange-400 bg-orange-50 text-orange-700 font-semibold"
                        : "border-gray-200 text-gray-600 hover:border-orange-200"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
          )}

          {analyzing && (
            <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="ticket-analyzing">
              <Loader2 size={14} className="animate-spin" />
              Analisando seu chamado com IA...
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="ticket-submit"
            className="w-full text-white font-semibold h-11"
            style={{ background: "#F97316" }}
          >
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
            Enviar chamado
          </Button>
        </form>
      </Card>
    </div>
  );
}
